/**
 * Сторінка відгуків клієнта
 * @component ReviewsPage
 * @file ReviewsPage.tsx
 * @path frontend/src/pages/ReviewsPage.tsx
 */

import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNotification } from '../../contexts/NotificationContext';
import { Star, Clock, User, Calendar, MessageSquare, CheckCircle, AlertCircle } from 'lucide-react';
import ReviewModal from '../../components/ReviewModal';
import './style.css';

const ReviewsPage: React.FC = () => {
  const { language } = useLanguage();
  const { addNotification } = useNotification();
  const [reviews, setReviews] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'pending' | 'completed'>('all');
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [appointmentToReview, setAppointmentToReview] = useState<any>(null);

  useEffect(() => {
    console.log('Reviews updated:', reviews.length, 'items');
    console.log('Completed reviews:', reviews.filter(r => r.rating > 0 || (r.reviewText && r.reviewText.trim() !== '')).length);
    console.log('Pending reviews:', reviews.filter(r => !(r.rating > 0 || (r.reviewText && r.reviewText.trim() !== ''))).length);
  }, [reviews]);

  useEffect(() => {
    fetchReviews();
  }, [filter]);

  const fetchReviews = async () => {
    console.log('fetchReviews called with filter:', filter);
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('auth_token');
      if (!token) {
        setError('Токен авторизації не знайдено');
        return;
      }

      // Fetch both reviews and completed appointments
      try {
        const [reviewsResponse, appointmentsResponse] = await Promise.all([
          fetch('/api/v1/reviews', {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }),
          fetch('/api/v1/client/appointments', {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          })
        ]);

        let allReviews: any[] = [];

        // Process existing reviews
        if (reviewsResponse.ok) {
          const reviewsData = await reviewsResponse.json();
          const transformedReviews = (reviewsData.reviews || []).map((review: any) => {
            console.log(`Review ${review.id}: rating=${review.rating}, reviewText="${review.comment}"`);
            return {
              id: review.id,
              appointmentId: review.appointment_id,
              serviceName: review.service_name || 'Послуга',
              masterName: review.employee_name || 'Майстер',
              masterAvatar: '/api/placeholder/40/40',
              date: new Date(review.created_at).toLocaleDateString('uk-UA'),
              time: new Date(review.created_at).toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' }),
              rating: review.rating,
              reviewText: review.comment || '',
              status: 'completed',
              salonName: review.business_name || 'Салон',
              price: 0
            };
          });
          allReviews = [...allReviews, ...transformedReviews];
        }

        // Process completed appointments that don't have reviews yet
        if (appointmentsResponse.ok) {
          const appointmentsData = await appointmentsResponse.json();
          const reviewedAppointmentIds = new Set(allReviews.map(r => r.appointmentId));
          
          const now = new Date();
          
          const pendingReviews = (appointmentsData.appointments || [])
            .filter((apt: any) => {
              // Show completed appointments that don't have reviews yet
              console.log(`Raw date for ${apt.id}:`, apt.date, typeof apt.date);
              const appointmentDateTime = new Date(apt.date);
              const hasReview = reviewedAppointmentIds.has(apt.id);
              const isPast = appointmentDateTime < now;
              const isCompleted = apt.status === 'completed';
              const isValidDate = !isNaN(appointmentDateTime.getTime());
              console.log(`Appointment ${apt.id}: date=${apt.date}, parsedDate=${appointmentDateTime}, isValid=${isValidDate}, status=${apt.status}, isPast=${isPast}, isCompleted=${isCompleted}, hasReview=${hasReview}`);
              return !hasReview && (isValidDate && isPast || isCompleted);
            })
            .map((apt: any) => {
              const appointmentDateTime = new Date(apt.date);
              const isValidDate = !isNaN(appointmentDateTime.getTime());
              return {
                id: `pending-${apt.id}`,
                appointmentId: apt.id,
                serviceName: apt.serviceName || 'Послуга',
                masterName: apt.masterName || 'Майстер',
                masterAvatar: '/api/placeholder/40/40',
                date: isValidDate ? appointmentDateTime.toLocaleDateString('uk-UA') : 'Невідома дата',
                time: apt.time || '',
                rating: 0,
                reviewText: null,
                status: 'pending',
                salonName: apt.salonName || 'Салон',
                price: apt.price || 0
              };
            })
          allReviews = [...allReviews, ...pendingReviews];
        }

        // Sort by date (newest first) and remove duplicates
        const uniqueReviews = allReviews.filter((review, index, self) => 
          index === self.findIndex((r) => r.appointmentId === review.appointmentId)
        );
        uniqueReviews.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        console.log('Unique reviews after deduplication:', uniqueReviews.length);
        setReviews(uniqueReviews);
        return;
      } catch (apiError) {
        console.log('API не доступний, використовуємо мок дані');
      }

      // Якщо API недоступний, використовуємо мок дані
      await new Promise(resolve => setTimeout(resolve, 1000)); // Імітація завантаження
      
      const mockData = [
        {
          id: '1',
          appointmentId: 'apt1',
          serviceName: 'Стрижка та укладання',
          masterName: 'Анна Петренко',
          masterAvatar: '/api/placeholder/40/40',
          date: '15.01.2024',
          time: '14:30',
          rating: 5,
          reviewText: 'Чудовий майстер! Анна дуже професійна, зробила ідеальну стрижку. Рекомендую всім!',
          status: 'completed',
          salonName: 'Beauty Salon',
          price: 450
        },
        {
          id: '2',
          appointmentId: 'apt2',
          serviceName: 'Манікюр з покриттям',
          masterName: 'Олена Ковальчук',
          masterAvatar: '/api/placeholder/40/40',
          date: '10.01.2024',
          time: '11:00',
          rating: 0,
          reviewText: null,
          status: 'pending',
          salonName: 'Nail Studio',
          price: 280
        },
        {
          id: '3',
          appointmentId: 'apt3',
          serviceName: 'Масаж обличчя',
          masterName: 'Марія Іваненко',
          masterAvatar: '/api/placeholder/40/40',
          date: '05.01.2024',
          time: '16:00',
          rating: 4,
          reviewText: 'Дуже приємний масаж, шкіра стала м\'якою. Єдине - довго чекала на початок процедури.',
          status: 'completed',
          salonName: 'SPA Center',
          price: 600
        },
        {
          id: '4',
          appointmentId: 'apt4',
          serviceName: 'Фарбування волосся',
          masterName: 'Ірина Сидоренко',
          masterAvatar: '/api/placeholder/40/40',
          date: '28.12.2023',
          time: '13:30',
          rating: 5,
          reviewText: 'Ірина - майстер свого діла! Колір вийшов саме таким, як я хотіла. Дуже дякую!',
          status: 'completed',
          salonName: 'Hair Studio',
          price: 800
        }
      ];

      setReviews(mockData);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      setError('Не вдалося завантажити відгуки. Спробуйте оновити сторінку.');
    } finally {
      setLoading(false);
    }
  };

  const handleWriteReview = (appointmentId: string) => {
    // Find appointment details from reviews list
    const appointment = reviews.find(r => r.appointmentId === appointmentId);
    if (appointment && !appointment.reviewText) {
      setAppointmentToReview(appointment);
      setReviewModalOpen(true);
    } else {
      // If review already exists, show notification and refresh data
      addNotification(
        language === 'ua' ? 'ℹ️ Відгук вже існує' : 'ℹ️ Review already exists',
        'info'
      );
      fetchReviews();
    }
  };


  // Close review modal
  const closeReviewModal = () => {
    setReviewModalOpen(false);
    setAppointmentToReview(null);
    // Refresh reviews when modal closes to ensure data is up to date
    fetchReviews();
  };

  // Submit review
  const submitReview = async (reviewData: { rating: number; comment: string }) => {
    const token = localStorage.getItem('auth_token');
    if (!token || !appointmentToReview) {
      throw new Error(language === 'ua' ? 'Токен авторизації відсутній' : 'Authorization token missing');
    }

    const response = await fetch('/api/v1/reviews', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        appointment_id: appointmentToReview.appointmentId,
        rating: reviewData.rating,
        comment: reviewData.comment || null,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      
      // Handle specific case where review already exists
      if (response.status === 409 && errorData.error?.includes('already exists')) {
        // Refresh reviews to get the latest state and close modal
        await fetchReviews();
        closeReviewModal();
        addNotification(
          language === 'ua' ? 'ℹ️ Відгук вже існує' : 'ℹ️ Review already exists',
          'info'
        );
        return; // Don't throw error, just handle gracefully
      }
      
      throw new Error(errorData.error || 'Failed to submit review');
    }

    // Refresh reviews to update UI
    await fetchReviews();
    addNotification(
      language === 'ua' ? '✅ Відгук успішно додано' : '✅ Review added successfully',
      'success'
    );
  };

  const filteredReviews = reviews.filter(review => {
    const hasReview = review.rating > 0 || (review.reviewText && review.reviewText.trim() !== '');
    console.log(`Filtering review ${review.id}: rating=${review.rating}, reviewText="${review.reviewText}", hasReview=${hasReview}, filter=${filter}`);
    if (filter === 'all') return true;
    if (filter === 'pending') return !hasReview;
    if (filter === 'completed') return hasReview;
    return true;
  });

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        size={16}
        className={i < rating ? 'star-filled' : 'star-empty'}
        fill={i < rating ? '#ffd700' : 'none'}
        color={i < rating ? '#ffd700' : '#ddd'}
      />
    ));
  };

  const renderReviewCard = (review: any) => (
    <div key={review.id} className="review-card">
      <div className="review-header">
        <div className="service-info">
          <div className="service-header">
            <h4>{review.serviceName}</h4>
            <div className="price">₴{review.price}</div>
          </div>
            <div className="salon-name">{review.salonName}</div>
            <div className="master-info">
              <div className="master-avatar">
                {review.is_master_business ? (
                  <i className="bi bi-person" style={{ fontSize: '20px' }}></i>
                ) : (
                  <User size={20} />
                )}
              </div>
              <div className="master-details">
                <p className="master-name">
                  {review.is_master_business ? (
                    <>
                      <i className="bi bi-person" style={{ marginRight: '6px', fontSize: '14px' }}></i>
                      {language === 'ua' ? 'Майстер:' : 'Master:'} {review.masterName}
                    </>
                  ) : (
                    <>
                      <i className="bi bi-shop" style={{ marginRight: '6px', fontSize: '14px' }}></i>
                      {language === 'ua' ? 'Бізнес:' : 'Business:'} {review.salonName}
                    </>
                  )}
                </p>
                <div className="appointment-datetime">
                  <Calendar size={14} />
                  <span>{review.date}</span>
                  <Clock size={14} />
                  <span>{review.time}</span>
                </div>
              </div>
            </div>
          </div>
        <div className="review-status">
          {(review.rating > 0 || (review.reviewText && review.reviewText.trim() !== '')) ? (
            <div className="status-badge completed">
              <CheckCircle size={14} />
              <span>{language === 'ua' ? 'Відгук залишено' : 'Review Submitted'}</span>
            </div>
          ) : (
            <div className="status-badge pending">
              <AlertCircle size={14} />
              <span>{language === 'ua' ? 'Очікує відгуку' : 'Pending Review'}</span>
            </div>
          )}
        </div>
      </div>

      {(review.rating > 0 || (review.reviewText && review.reviewText.trim() !== '')) ? (
        <div className="review-content">
          <div className="rating">
            {renderStars(review.rating)}
            <span className="rating-number">{review.rating}.0</span>
          </div>
          {review.reviewText && (
            <p className="review-text">{review.reviewText}</p>
          )}
        </div>
      ) : (
        <div className="review-prompt">
          <div className="prompt-content">
            <MessageSquare size={24} className="prompt-icon" />
            <p>
              {language === 'ua'
                ? 'Поділіться своїм досвідом! Залиште відгук про цю послугу.'
                : 'Share your experience! Leave a review for this service.'
              }
            </p>
          </div>
          <button
            className="write-review-btn"
            onClick={() => handleWriteReview(review.appointmentId)}
          >
            <Star size={16} />
            {language === 'ua' ? 'Написати відгук' : 'Write Review'}
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="reviews-page">
      <div className="page-header">
        <h1 className="page-title">
          {language === 'ua' ? 'Мої відгуки' : 'My Reviews'}
        </h1>
        <p className="page-subtitle">
          {language === 'ua'
            ? 'Перегляньте та залиште відгуки про послуги'
            : 'View and leave reviews about services'
          }
        </p>
      </div>

      <div className="page-content">
        {/* Tab Navigation */}
        <div className="tabs-navigation">
          <button
            className={`tab-button ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            <i className="bi bi-list-ul"></i>
            <span className="tab-text">
              {language === 'ua' ? 'Всі' : 'All'} ({reviews.length})
            </span>
          </button>
          <button
            className={`tab-button ${filter === 'pending' ? 'active' : ''}`}
            onClick={() => setFilter('pending')}
          >
            <i className="bi bi-clock"></i>
            <span className="tab-text">
              {language === 'ua' ? 'Очікують відгуку' : 'Pending Review'} ({reviews.filter(r => !(r.rating > 0 || (r.reviewText && r.reviewText.trim() !== ''))).length})
            </span>
          </button>
          <button
            className={`tab-button ${filter === 'completed' ? 'active' : ''}`}
            onClick={() => setFilter('completed')}
          >
            <i className="bi bi-star-fill"></i>
            <span className="tab-text">
              {language === 'ua' ? 'З відгуками' : 'With Reviews'} ({reviews.filter(r => r.rating > 0 || (r.reviewText && r.reviewText.trim() !== '')).length})
            </span>
          </button>
        </div>

        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>{language === 'ua' ? 'Завантаження відгуків...' : 'Loading reviews...'}</p>
          </div>
        ) : error ? (
          <div className="error-state">
            <AlertCircle size={48} className="error-icon" />
            <h3>{language === 'ua' ? 'Помилка завантаження' : 'Loading Error'}</h3>
            <p>{error}</p>
            <button onClick={fetchReviews} className="retry-btn">
              {language === 'ua' ? 'Спробувати знову' : 'Retry'}
            </button>
          </div>
        ) : filteredReviews.length === 0 ? (
          <div className="empty-state">
            <i className="bi bi-star"></i>
            <h3>
              {language === 'ua' ? 'Немає відгуків' : 'No reviews yet'}
            </h3>
            <p>
              {language === 'ua'
                ? 'Ваші відгуки будуть відображатися тут'
                : 'Your reviews will appear here'
              }
            </p>
          </div>
        ) : (
          <div className="reviews-list">
            {filteredReviews.map((review) => renderReviewCard(review))}
          </div>
        )}
      </div>

      {/* Review Modal */}
      {appointmentToReview && (
        <ReviewModal
          isOpen={reviewModalOpen}
          onClose={closeReviewModal}
          onSubmit={submitReview}
          appointmentDetails={{
            serviceName: appointmentToReview.serviceName,
            masterName: appointmentToReview.masterName,
            businessName: appointmentToReview.salonName,
            appointmentId: appointmentToReview.appointmentId
          }}
        />
      )}
    </div>
  );
};

export default ReviewsPage;
